import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { MapPin, Calendar, Tag, ArrowRight } from 'lucide-react';
import { useT } from '../context/LanguageContext';
import { ui, portfolioData } from '../translations';

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('visible'); obs.unobserve(el); } }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
};

const PortfolioPage = () => {
  const t = useT();
  const lang = t({ ro: 'ro', en: 'en' });
  useSEO({
    title: lang === 'ro'
      ? 'Portofoliu Proiecte | Securitate, Smart Home, IT | Bee-Advised România'
      : 'Project Portfolio | Security, Smart Home, IT | Bee-Advised Romania',
    description: lang === 'ro'
      ? 'Proiecte realizate de Bee-Advised: sisteme CCTV, securitate corporativă, automatizare vile, infrastructură rețea, BMS și smart home în București, Cluj, Timișoara și toată România.'
      : 'Projects by Bee-Advised: CCTV systems, corporate security, villa automation, network infrastructure, BMS and smart home in Bucharest, Cluj, Timisoara and all Romania.',
    canonical: 'https://www.bee-advised.ro/portofoliu',
    lang,
  });
  const [activeFilter, setActiveFilter] = useState('all');
  const gridRef = useReveal();

  const filters = [
    { key: 'all', label: t(ui.portfolioPage.filterAll) },
    { key: 'security', label: t(ui.portfolioPage.filterSecurity) },
    { key: 'surveillance', label: t(ui.portfolioPage.filterSurveillance) },
    { key: 'infrastructure', label: t(ui.portfolioPage.filterInfra) },
    { key: 'smart', label: t(ui.portfolioPage.filterSmart) },
  ];

  const filtered = activeFilter === 'all' ? portfolioData : portfolioData.filter(p => p.category === activeFilter);

  const catColorMap = {
    security: 'bg-blue-50 text-blue-700 border-blue-200',
    surveillance: 'bg-purple-50 text-purple-700 border-purple-200',
    infrastructure: 'bg-green-50 text-green-700 border-green-200',
    smart: 'bg-orange-50 text-orange-700 border-orange-200',
  };

  return (
    <div>
      {/* PAGE HERO */}
      <section data-testid="portfolio-hero" className="relative bg-[#0A192F] hex-pattern pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-badge"><span className="w-8 h-px bg-[#00BCD4]"/>{t(ui.portfolioPage.badge)}</div>
          <h1 className="text-5xl md:text-6xl font-black text-white font-heading tracking-tight leading-none mt-3">
            {t(ui.portfolioPage.title)}
          </h1>
          <p className="text-slate-400 mt-4 text-lg max-w-2xl leading-relaxed">{t(ui.portfolioPage.subtitle)}</p>
        </div>
      </section>

      {/* FILTER */}
      <section className="bg-white border-b border-slate-100 sticky top-[72px] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto py-3">
            {filters.map(f => (
              <button key={f.key} data-testid={`portfolio-filter-${f.key}`}
                onClick={() => setActiveFilter(f.key)}
                className={`px-5 py-2 text-sm font-semibold uppercase tracking-wider whitespace-nowrap transition-all duration-200 ${activeFilter === f.key ? 'bg-[#0A192F] text-[#00BCD4]' : 'text-slate-500 hover:text-[#0A192F] hover:bg-slate-50'}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={gridRef} className="reveal grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map(project => (
              <article key={project.id} data-testid={`project-card-${project.id}`}
                className="group bg-white border border-slate-200 hover:border-[#00BCD4] overflow-hidden transition-all duration-300 hover:-translate-y-1 flex flex-col"
                style={{ boxShadow: '0 0 0 0 rgba(0,240,255,0)', transition: 'all 0.3s ease' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,240,255,0.1)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 0 0 0 rgba(0,240,255,0)'}>
                <div className="relative h-52 overflow-hidden">
                  <img src={project.image} alt={t(project.title)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/70 via-transparent to-transparent"/>
                  <div className="absolute top-3 right-3">
                    <span className={`text-xs px-2 py-1 border font-semibold ${catColorMap[project.category] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                      {t(filters.find(f => f.key === project.category)?.label || '')}
                    </span>
                  </div>
                  <div className="absolute bottom-3 left-4 flex gap-2 flex-wrap">
                    {project.tags.slice(0, 2).map((tag, i) => (
                      <span key={i} className="text-xs bg-[#00BCD4]/20 border border-[#00BCD4]/40 text-[#00BCD4] px-2 py-0.5">{t(tag)}</span>
                    ))}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-4 mb-3">
                    <span className="flex items-center gap-1 text-xs text-slate-400">
                      <MapPin size={12}/> {project.location}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-slate-400">
                      <Calendar size={12}/> {project.year}
                    </span>
                  </div>
                  <h3 className="font-bold text-[#0A192F] text-base font-heading mb-2">{t(project.title)}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed flex-1">{t(project.desc)}</p>
                  <div className="flex flex-wrap gap-1 mt-4">
                    {project.tags.map((tag, i) => (
                      <span key={i} className="text-xs bg-[#F8FAFC] border border-slate-200 text-slate-500 px-2 py-0.5 flex items-center gap-1">
                        <Tag size={10}/> {t(tag)}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20 text-slate-400">
              <p>{t({ ro: 'Niciun proiect în această categorie.', en: 'No projects in this category.' })}</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0A192F] hex-pattern">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white font-heading">{t(ui.cta.title)}</h2>
          <p className="text-slate-400 mt-4 text-lg">{t(ui.cta.subtitle)}</p>
          <div className="flex flex-wrap gap-4 mt-8 justify-center">
            <Link to="/contact" className="btn-primary">{t(ui.cta.btn1)} <ArrowRight size={16}/></Link>
            <a href="tel:+40744324272" className="btn-primary-outline">{t(ui.cta.btn2)}</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PortfolioPage;
